import { useParams, useNavigate, Link } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useArticleForCheckout } from '@/hooks/useCheckout';
import { getArticlePrice, formatPrice } from '@/lib/stripe';
import { CheckoutForm } from '@/components/checkout/CheckoutForm';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronRight, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
  console.error('[Checkout] ❌ Stripe publishable key is missing!');
}

const stripePromise = loadStripe(stripeKey);

export function CheckoutPage() {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('checkout');
  const currentLocale = locale || i18n.language || 'fr';

  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const { data: article, isLoading: articleLoading } = useArticleForCheckout(slug || '', currentLocale);

  if (!slug) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-aspexit-gray">{t('errors.notFound')}</p>
      </div>
    );
  }

  if (articleLoading) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-aspexit-gray">{t('errors.loading')}</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-heading font-extrabold text-aspexit-black mb-6">
          {t('errors.unavailable.title')}
        </h1>
        <p className="text-aspexit-gray mb-6">
          {t('errors.unavailable.description')}
        </p>
        <Button asChild>
          <Link to={`/${currentLocale}/blog`}>{t('errors.unavailable.backButton')}</Link>
        </Button>
      </div>
    );
  }

  // Prix unique fixe
  const priceInCents = getArticlePrice(0);

  // Payment success screen
  if (paymentSuccess) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-aspexit-green text-white rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-heading font-extrabold text-aspexit-black mb-8 text-center">
            {t('success.title')}
          </h1>

          {/* Info Cards */}
          <div className="space-y-4 mb-8">
            {/* Email confirmation */}
            <Card className="p-6 bg-gradient-to-r from-aspexit-green/5 to-aspexit-green/10 border-aspexit-green/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-aspexit-green rounded-full flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-aspexit-black mb-2">
                    {t('success.email.title')}
                  </h3>
                  <p className="text-sm text-aspexit-gray" dangerouslySetInnerHTML={{ __html: t('success.email.description') }} />
                </div>
              </div>
            </Card>

            {/* Account access */}
            {!user ? (
              <Card className="p-6 bg-gradient-to-r from-blue-50 to-white border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-aspexit-black mb-2">
                      {t('success.account.guest.title')}
                    </h3>
                    <p className="text-sm text-aspexit-gray" dangerouslySetInnerHTML={{ __html: t('success.account.guest.description') }} />
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-6 bg-gradient-to-r from-blue-50 to-white border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-aspexit-black mb-2">
                      {t('success.account.authenticated.title')}
                    </h3>
                    <p className="text-sm text-aspexit-gray">
                      {t('success.account.authenticated.description')}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="xl" variant="default">
              <Link to={`/${currentLocale}/blog`}>
                {t('success.actions.backToBlog')}
              </Link>
            </Button>
            {user ? (
              <Button asChild size="xl" variant="outline-red">
                <Link to={`/${currentLocale}/profile`}>{t('success.actions.viewPurchases')}</Link>
              </Button>
            ) : (
              <Button asChild size="xl" variant="outline-red">
                <Link to={`/${currentLocale}/auth/login`}>
                  {t('success.actions.login')}
                </Link>
              </Button>
            )}
          </div>

          {/* Additional info */}
          <p className="text-center text-sm text-aspexit-gray mt-8">
            {t('success.support')} <Link to={`/${currentLocale}/contact`} className="text-aspexit-green hover:underline font-medium">{t('success.contactLink')}</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-aspexit-gray">
            <Link to={`/${currentLocale}`} className="hover:text-aspexit-green">
              {t('breadcrumb.home')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/${currentLocale}/blog`} className="hover:text-aspexit-green">
              {t('breadcrumb.blog')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/${currentLocale}/blog/${slug}`} className="hover:text-aspexit-green">
              {article.title}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-aspexit-black">{t('breadcrumb.payment')}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/${currentLocale}/blog/${slug}`)}
            className="mb-6 -ml-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('page.backToArticle')}
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column: Article Info */}
            <div>
              <h1 className="text-3xl font-heading font-extrabold text-aspexit-black mb-6">
                {t('page.title')}
              </h1>

              <Card className="p-6 mb-6">
                <div className="flex items-start gap-4 mb-4">
                  {article.featured_image ? (
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-20 h-20 rounded object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded bg-gradient-to-br from-aspexit-green to-aspexit-green-dark flex items-center justify-center text-white font-bold text-2xl">
                      {article.title.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1">
                    <h2 className="font-semibold text-aspexit-black mb-1">
                      {article.title}
                    </h2>
                    <p className="text-sm text-aspexit-gray line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="secondary">{article.category}</Badge>
                  <Badge variant="outline">
                    {article.reading_time} {t('page.readingTime')}
                  </Badge>
                  <Badge variant="outline" className="bg-aspexit-green/10 text-aspexit-green border-aspexit-green">
                    {t('page.badge.premium')}
                  </Badge>
                </div>

                <div className="bg-aspexit-gray-light rounded-lg p-4">
                  <h3 className="font-semibold text-aspexit-black mb-2">
                    {t('page.benefits.title')}
                  </h3>
                  <ul className="space-y-2 text-sm text-aspexit-gray">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-aspexit-green flex-shrink-0 mt-0.5" />
                      <span>{t('page.benefits.unlimitedAccess')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-aspexit-green flex-shrink-0 mt-0.5" />
                      <span>{t('page.benefits.pdfDownload')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-aspexit-green flex-shrink-0 mt-0.5" />
                      <span>{t('page.benefits.permanentAccess')}</span>
                    </li>
                  </ul>
                </div>
              </Card>

              {/* Tarif unique */}
              <Card className="p-6 bg-gradient-to-br from-aspexit-green/5 via-white to-aspexit-green/10 border-2 border-aspexit-green/20 mt-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-aspexit-black">
                    {t('page.pricing.title')}
                  </h3>
                  <span className="font-bold text-2xl text-aspexit-green">
                    {formatPrice(priceInCents)}
                  </span>
                </div>
              </Card>
            </div>

            {/* Right Column: Payment Form */}
            <div>
              <Card className="p-6 sticky top-4">
                <div className="flex items-center gap-2 mb-6 text-aspexit-gray">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">{t('page.security.securePayment')}</span>
                </div>

                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    articleId={article.id}
                    articleTitle={article.title}
                    basePrice={priceInCents}
                    user={user}
                    onSuccess={(purchaseId) => {
                      console.log('Payment successful, purchase ID:', purchaseId);
                      setPaymentSuccess(true);
                      // Scroll to top to show success message
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  />
                </Elements>
              </Card>

              {/* Trust signals */}
              <div className="mt-6 grid grid-cols-3 gap-4 text-center text-xs text-aspexit-gray">
                <div>
                  <div className="text-2xl mb-1">🔒</div>
                  <p>{t('page.security.securePayment')}</p>
                </div>
                <div>
                  <div className="text-2xl mb-1">⚡</div>
                  <p>{t('page.security.instantAccess')}</p>
                </div>
                <div>
                  <div className="text-2xl mb-1">💯</div>
                  <p>{t('page.security.satisfaction')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
